// Yoga Bath - SEO-optimized location page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaBath = () => {
  usePageMeta({
    title: "Yoga Classes Bath | Annelie Lily",
    description: "Looking for yoga classes near Bath? Small group yoga in Frome, just 25 minutes away. Hatha, Yin & private sessions with British Wheel of Yoga teacher",
    canonical: "https://annelielily.com/areas/yoga-bath"
  });

  const locationSchema = generateLocationSchema({
    townName: "Bath",
    description: "Yoga classes near Bath at Annelie Lily Yoga in Frome. Small group Hatha and Yin yoga classes, private sessions, and holistic treatments just 25 minutes from Bath city centre.",
    url: "https://annelielily.com/yoga-bath"
  });

  const faqs = [
    {
      question: "What yoga classes are available for Bath residents?",
      answer: "I offer Hatha Yoga, Yin Yoga, Aerial Yoga, and private 1-to-1 sessions. All classes are held in Frome, Somerset, about 25 minutes from Bath."
    },
    {
      question: "How do I get to Frome from Bath?",
      answer: "Take the A36 south through Woolverton, then join the A361 into Frome. It's a straightforward 25-minute drive, mostly single carriageway and quite scenic."
    },
    {
      question: "Are classes suitable for beginners?",
      answer: "Yes, all my classes welcome beginners. I teach in a way that allows everyone to work at their own level, with modifications offered throughout."
    },
    {
      question: "How big are the classes?",
      answer: "Maximum 8 people. This keeps things personal — small enough that I can offer individual guidance, but with the energy of a group."
    },
    {
      question: "Do you offer treatments as well as yoga?",
      answer: "Yes. I'm also trained in Swedish massage, Reiki, and chakra healing. Many students combine yoga with occasional treatments — they complement each other well."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Bath" currentSlug="areas/yoga-bath" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Yoga for Bath
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Yoga Classes Bath
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Small, welcoming yoga classes in Frome, just 25 minutes from Bath. 
              A calm and supportive environment with space for individual guidance.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                Based in Frome
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Car className="w-4 h-4 text-gold" />
                25 min from Bath
              </span>
            </div>
            
            <Link to="/contact" className="btn-primary">
              Book a Class <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* About Yoga in Frome */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Yoga for Bath Residents
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Based in Frome, about 25 minutes from Bath, I offer small group <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> in a calm, supportive environment. Classes are kept small — usually around 6-8 
                people — so there's space for individual guidance and attention.
              </p>
              <p>
                The journey through the Somerset countryside can be a lovely way to transition 
                from the busyness of the day. Many of my students from Bath enjoy arriving 
                already feeling a little more settled.
              </p>
              <p>
                Whether you're looking for <Link to="/areas/yoga-bath/hatha-yoga" className="text-gold hover:underline">Hatha yoga in Bath</Link>, <Link to="/areas/yoga-bath/yin-yoga" className="text-gold hover:underline">Yin yoga near Bath</Link>, or <Link to="/areas/yoga-bath/private-yoga" className="text-gold hover:underline">private yoga sessions</Link>, you're 
                welcome to come and try a class. <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">New to yoga?</Link> That's perfectly fine — all levels are welcome.
              </p>
              <p>
                The main yoga styles I teach are <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha Yoga</Link> (active, strength-building) and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin Yoga</Link> (slower, deeply restorative).
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Getting Here */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Getting to Frome from Bath
            </h2>
            <div className="bg-white/70 p-6 md:p-8 rounded-xl border border-gold/10 space-y-4">
              <p className="text-neutral-700">
                <strong>Route:</strong> Take the A36 south through Woolverton, then join the A361 into Frome. 
                Straightforward, mostly single carriageway, quite scenic.
              </p>
              <p className="text-neutral-700">
                <strong>Time:</strong> About 25 minutes in normal traffic. Slightly longer in rush hour, 
                but if you're coming for an evening class, traffic has usually cleared.
              </p>
              <p className="text-neutral-700">
                <strong>Parking:</strong> Available near the studio — I'll send you directions when you book.
              </p>
              <p className="text-neutral-700">
                <strong>From which parts of Bath?</strong> I have students from the city centre, Batheaston, 
                Bathampton, Combe Down, Odd Down. Wherever you are in Bath, it's a manageable distance.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Available */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Yoga Classes for Bath
            </h2>
            <p className="text-neutral-600">
              All classes held in Frome, Somerset — just 25 minutes from Bath
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Hatha Yoga Bath",
                desc: "Traditional yoga practice — postures, breathing, relaxation. Good for building strength and flexibility.",
                link: "/areas/yoga-bath/hatha-yoga"
              },
              {
                title: "Yin Yoga Bath",
                desc: "Slower, deeper. Long-held poses targeting connective tissue. Brilliant for stress and tight hips.",
                link: "/areas/yoga-bath/yin-yoga"
              },
              {
                title: "Private Sessions Bath",
                desc: "One-to-one yoga designed around you. Useful for injuries, specific goals, or simply preference.",
                link: "/areas/yoga-bath/private-yoga"
              },
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={service.link}
                  className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all h-full group"
                >
                  <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                  <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Questions from Bath Students
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                  <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                  <p className="text-neutral-600 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Try a Yoga Class?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              If you're curious about making the trip from Bath, get in touch. I can tell you 
              about upcoming classes and help you figure out if this is what you're looking for.
            </p>
            <Link to="/contact" className="btn-primary">
              Book a Class <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Other Areas */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">
            Also serving yoga classes near:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Warminster", link: "/areas/yoga-warminster" },
              { name: "Westbury", link: "/areas/yoga-westbury" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Wells", link: "/areas/yoga-wells" },
              { name: "Radstock", link: "/areas/yoga-radstock" },
            ].map((area) => (
              <Link
                key={area.name}
                to={area.link}
                className="text-sm text-gold hover:underline underline-offset-4"
              >
                {area.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaBath;
